import { clamp } from '../core/math';
import { ArmWrestleGame, BarOpponent } from '../minigames/armwrestle';
import { DartsGame } from '../minigames/darts';
import { VideoPokerGame } from '../minigames/poker';
import type { MiniResult } from '../minigames/types';
import { ArmStage } from '../stages/ArmStage';
import { BullStage, type BullMount } from '../stages/BullStage';
import { DartsStage } from '../stages/DartsStage';
import { ScreenStage } from '../stages/ScreenStage';
import { BAR_SPOTS, DARTBOARD_RADIUS, type BarRoom } from '../world/BarRoom';
import { ShootingStage } from '../stages/ShootingStage';
import { usedToday, useToday } from '../state/daily';
import type { StageHost } from '../ui/StageHost';
import type { GameServices } from './services';

export type GameKind = 'darts' | 'armwrestle' | 'poker' | 'mechbull' | 'rodeo' | 'shoot';

/** Who you play against at the Rusty Spur. */
export const BAR_RIVALS = {
  darts: { name: 'Lurlene', skill: 0.55 },
  armwrestle: { name: 'Big Tiny', strength: 0.82 },
} as const;

export const RODEO_PURSE = 150;
export const RODEO_ENTRY = 15;
export const BULL_PRICE = 5;
export const SHELLS_PRICE = 8;
export const RANGE_PRIZE = 25;
/** Points for Gus's daily top-shot prize. */
export const RANGE_PRIZE_SCORE = 15;

/**
 * Starts the bar games, the rodeo and the gun range, takes your money up front and
 * pays out after. Kept out of the content files so NPCs can just say "play darts for $10".
 */
export class Games {
  constructor(
    private readonly s: GameServices,
    private readonly stages: StageHost,
    private readonly hooks: {
      /** The bulls you can climb on: the mechanical one at the bar, Widowmaker at the rodeo. */
      mount(kind: 'mechanical' | 'rodeo'): BullMount;
      /** The Rusty Spur, for its dartboard, poker machine and arm-wrestling table. */
      bar(): BarRoom;
    },
  ) {}

  /** Your arm: clout helps, booze doesn't (past a point). */
  private armPower(): number {
    const { store } = this.s;
    return 1 + Math.min(0.3, store.rating() / 500) - clamp((store.stat('buzz') - 60) / 40, 0, 1) * 0.25;
  }

  play(kind: GameKind, bet = 0): void {
    const { store, rng, ui } = this.s;
    if (bet > 0 && !store.spend(bet)) {
      store.toast(`That's $${bet}. You don't have $${bet}.`, 'bad', ':money-wings:');
      return;
    }
    const buzz = store.stat('buzz');
    const done = (result: MiniResult): MiniResult => {
      window.setTimeout(() => ui.hud.update(), 0);
      return result;
    };
    switch (kind) {
      case 'darts': {
        const r = BAR_RIVALS.darts;
        const bar = this.hooks.bar();
        const d = BAR_SPOTS.darts;
        const stage = new DartsStage(
          {
            board: bar.dartboard,
            chalk: bar.chalk,
            radius: DARTBOARD_RADIUS,
            oche: bar.world(d),
            facing: -Math.PI / 2,
            rival: this.s.npcs.get('lurlene') ?? null,
            rivalSpot: bar.world({ x: d.x + 0.5, z: d.z + 1.2 }),
          },
          this.s.player,
          (n) => this.s.sfx(n),
          new DartsGame(rng, r.name, r.skill, buzz),
          this.s.view,
        );
        this.stages.open(stage, (st, bailed) => {
          const g = (st as DartsStage).game;
          if (bailed)
            return done({
              title: 'Walked Off',
              art: ':target:',
              text: `${r.name} keeps your $${bet}. "Forfeit's a forfeit, sugar."`,
            });
          if (g.won) {
            store.earn(bet * 2);
            store.incr('dartsWins');
            store.addClout(3);
            if (store.counter('dartsWins') >= 5) store.unlock('bar_league');
            return done({
              title: 'Bullseye!',
              art: ':target:',
              text: `${g.score.you} to ${g.score.them}. ${r.name} slaps $${bet} on the bar and buys you a beer out of spite.`,
            });
          }
          if (g.score.you === g.score.them) {
            store.earn(bet);
            return done({
              title: 'Dead Heat',
              art: ':target:',
              text: `${g.score.you} apiece. Money back. "Again?" says ${r.name}. She means it as a threat.`,
            });
          }
          return done({
            title: 'Lurlene Wins',
            art: ':target:',
            text: `${g.score.you} to ${g.score.them}. "Don't feel bad," says ${r.name}. "I play on Tuesdays. And Thursdays. And most other days."`,
          });
        });
        return;
      }
      case 'armwrestle': {
        const r = BAR_RIVALS.armwrestle;
        useToday(store, 'armwrestle');
        const bar = this.hooks.bar();
        const t = BAR_SPOTS.armTable;
        const stage = new ArmStage(
          new ArmWrestleGame(r.name, new BarOpponent(rng, r.strength), this.armPower()),
          {
            table: bar.world(t),
            tableY: 0.775,
            you: bar.world({ x: t.x, z: t.z + 0.75 }),
            them: bar.world({ x: t.x, z: t.z - 0.75 }),
            seat: 0.74,
            rival: this.s.npcs.get('tiny') ?? null,
          },
          this.s.player,
          (n) => this.s.sfx(n),
        );
        this.stages.open(stage, (st, bailed) => {
          const g = (st as ArmStage).game;
          if (bailed && !g.over)
            return done({
              title: 'Tapped Out',
              art: ':flex:',
              text: `You let go and shake out your hand. ${r.name} keeps the $${bet}. "Smart," he says. It doesn't feel smart.`,
            });
          if (g.won) {
            store.earn(bet * 2);
            store.incr('armWins');
            store.addClout(5);
            store.unlock('arm_wrestle');
            return done({
              title: 'PINNED!',
              art: ':flex:',
              text: `${r.name} stares at his own knuckles on the table like they betrayed him. He pays $${bet} and asks for a rematch "when he's sober."`,
            });
          }
          return done({
            title: 'Pinned',
            art: ':flex:',
            text: `${r.name} folds your arm like a lawn chair. Your $${bet} goes in his tip jar. "Good effort," he says, which is worse.`,
          });
        });
        return;
      }
      case 'poker': {
        const bar = this.hooks.bar();
        const m = BAR_SPOTS.poker;
        const stage = new ScreenStage(
          new VideoPokerGame(rng, bet),
          { screen: bar.pokerScreenMesh, stand: bar.world({ x: m.x - 0.8, z: m.z }), facing: Math.PI / 2 },
          this.s.player,
        );
        this.stages.open(stage, (st) => {
          const g = (st as ScreenStage).game as VideoPokerGame;
          const pay = g.payout;
          if (pay > 0) {
            store.earn(pay);
            if (pay >= bet * 9) store.incr('pokerWins');
            if (g.result === 'Royal Flush') store.unlock('royal_flush');
            return done({
              title: g.result,
              art: ':cards:',
              text: `The machine dings like a church bell. +$${pay}. Somebody at the bar says "lucky" like it's a cuss word.`,
            });
          }
          return done({
            title: 'No Dice',
            art: ':cards:',
            text: 'The machine eats your money and plays a little song about it.',
          });
        });
        return;
      }
      case 'mechbull':
      case 'rodeo': {
        const rodeo = kind === 'rodeo';
        const stage = new BullStage(
          this.hooks.mount(rodeo ? 'rodeo' : 'mechanical'),
          this.s.player,
          rng,
          (n) => this.s.sfx(n),
          buzz,
        );
        this.stages.open(stage, (st, bailed) => {
          const g = (st as BullStage).game;
          if (bailed && !g.over)
            return done({
              title: 'Chickened Out',
              art: ':chicken:',
              text: rodeo
                ? `You slide off before the gate opens. Randy the clown keeps your $${bet} "for the bull's therapy."`
                : 'You climb back down before it even starts. Lurlene keeps the five bucks and the memory.',
            });
          store.incr('bullRides');
          const tenths = Math.round(g.time * 10);
          if (tenths > store.counter('bullBest')) store.state.counters.bullBest = tenths;
          if (g.qualified) {
            store.addClout(rodeo ? 12 : 4);
            store.unlock(rodeo ? 'eight_seconds' : 'mech_bull');
            if (rodeo) {
              const paid = usedToday(store, 'rodeo_purse') > 0;
              if (!paid) {
                useToday(store, 'rodeo_purse');
                store.earn(RODEO_PURSE);
                store.headline(
                  `RODEO: ${store.state.player.name.toUpperCase()} rides "Widowmaker" the full eight. Bull "embarrassed, honestly"`,
                );
              }
              return done({
                title: 'QUALIFIED RIDE!',
                art: ':cowboy:',
                text: paid
                  ? 'Eight seconds on Widowmaker. Again. The crowd loses its mind. The purse already went out today, but the glory is free.'
                  : `Eight seconds on Widowmaker! The crowd goes nuts and Randy the clown hands you the $${RODEO_PURSE} purse in a feed sack.`,
              });
            }
            return done({
              title: 'Eight Seconds!',
              art: ':bull:',
              text: 'You rode the mechanical bull the whole eight. The bar cheers. Lurlene turns it up to "divorce" for the next guy.',
            });
          }
          store.addStat('health', rodeo ? -12 : -4);
          return done({
            title: rodeo ? 'Bucked Off' : 'Thrown',
            art: rodeo ? ':hurt:' : ':knocked-out:',
            text: rodeo
              ? `${g.time.toFixed(1)} seconds. Widowmaker sends you into the next county. Randy the clown waves at you from the barrel. -12 :heart:`
              : `${g.time.toFixed(1)} seconds, then the inflatable pad. Everybody saw. Everybody filmed. -4 :heart:`,
          });
        });
        return;
      }
      case 'shoot': {
        const { world, player } = this.s;
        const stage = new ShootingStage(
          world.fair.range,
          player,
          this.s.view,
          rng,
          {
            sparks: world.sparks,
            smoke: world.smoke,
            sfx: (n) => this.s.sfx(n),
            float: (text, at, color) => ui.hud.float(text, at, color),
          },
          buzz,
        );
        this.stages.open(stage, (st, bailed) => {
          const g = st as ShootingStage;
          if (bailed)
            return done({
              title: 'Cease Fire',
              art: ':target:',
              text: `You hand the rifle back with ${g.shells} shells left. Gus counts them twice.`,
            });
          if (g.score > store.counter('shootScore')) store.state.counters.shootScore = g.score;
          store.addClout(Math.floor(g.score / 3));
          if (g.score >= RANGE_PRIZE_SCORE && usedToday(store, 'range_prize') === 0) {
            useToday(store, 'range_prize');
            store.earn(RANGE_PRIZE);
            store.unlock('dead_eye');
            return done({
              title: 'Dead Eye!',
              art: ':target:',
              text: `${g.score} points. Gus spits, nods, and peels $${RANGE_PRIZE} off a roll. "Daily top shot. Don't let it go to your head."`,
            });
          }
          return done({
            title: `${g.score} Points`,
            art: ':empty-can:',
            text:
              g.score >= RANGE_PRIZE_SCORE
                ? `${g.score} points. Top shot already got paid today, but Gus writes your name on the chalkboard anyway.`
                : g.score >= 8
                  ? `${g.score} points. Respectable. Gus nods once, which from Gus is a parade. (${RANGE_PRIZE_SCORE} takes the daily prize.)`
                  : `${g.score} points. Gus says the possums are safe from you. He means it kindly. Mostly.`,
          });
        });
        return;
      }
    }
  }
}
